import React from 'react';
import { useParams } from 'react-router';
import { defaultTo, get } from 'lodash';
import { ErrorMessage, Field } from '@atlaskit/form';
import { LoadingButton } from '@atlaskit/button';
import Textfield from '@atlaskit/textfield';
import SectionMessage from '@atlaskit/section-message';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { isJiraProduct } from '@atlassian/cssxp-utils-product';
import { ParentProductEntitlementTable } from './parent-product-entitlement-table';
import { useLinkParentProductToApp } from '../hooks/use-link-app-to-parent-product';
import { usePartnerCatalogueContext } from '../context/partner-catalogue-context';
import { ParentEntitlementDetails } from '../types';
import { formatLineItems } from '../utils';
import './link-app-to-parent-product.scss';

export type LinkAppToParentProductProps = {
  product: PartnerProductCatalogueItem;
  offeringLabel?: string;
};

export const LinkAppToParentProduct: React.FC<LinkAppToParentProductProps> = React.memo(
  ({ product, offeringLabel }) => {
    const [entitlementSearchKey, setEntitlementSearchKey] = React.useState<string>('');
    const [shouldHideChangeEntitlementButton, setShouldHideChangeEntitlementButton] = React.useState<boolean>(false);
    const [inputValidationError, setInputValidationError] = React.useState<string | null>(null);
    const { txaId } = useParams<{ txaId: string }>();
    const {
      setAppsParentEntitlement,
      setInvoiceGroupIdAttachedToExistingSite,
      isInvalidIGForAllExistingEntitlements,
      setIfCartItemValid,
      removeAppsParentEntitlement,
      quoteFormData,
      getSelectedPricingPlanForAProduct,
      allowedAppsParentEntitlements,
      cloudSitesEntitlements,
    } = usePartnerCatalogueContext();

    const lineItems = formatLineItems(quoteFormData, getSelectedPricingPlanForAProduct);

    const {
      data: entitlement,
      loading: entitlementSearchLoading,
      entitlementValidationError,
      isMaxUsersDetailLoading,
      isNotParentProductCompatible,
      isEnabledEntitlementChangeButton,
      searchEntitlement,
      setIsNotParentProductCompatible,
      setEnabledEntitlementChangeButton,
      setEntitlementDetails,
      setIsReadyToFetchMaxUsersDetail,
    } = useLinkParentProductToApp({
      txaId,
      appProductKey: product.id,
    });

    React.useEffect(() => {
      setShouldHideChangeEntitlementButton(false);
      // taking all available products from the offering and checking if any of the entitlements is compatible with the available products
      const availableProducts = product?.offering?.flatMap((offering: any) => offering?.availableProducts ?? []);
      for (const key in cloudSitesEntitlements) {
        if (cloudSitesEntitlements[key]) {
          // checking if product is available in the cloud site entitlements
          const isProductAvailableInCloudSite = availableProducts?.some((productId) => {
            return productId === key; // key is the jira container relationship product id or product id
          });
          if (isProductAvailableInCloudSite) {
            const entitlement = cloudSitesEntitlements[key];
            setEntitlementDetails(entitlement);
            setShouldHideChangeEntitlementButton(true);
            setIsNotParentProductCompatible(false);
            setEnabledEntitlementChangeButton(true);
            setAppsParentEntitlement(product?.id, entitlement);
            setIfCartItemValid(product?.id, true);
          }
        }
      }
    }, [cloudSitesEntitlements, product?.offering]);

    React.useEffect(() => {
      // this condition is to reset invoice group id
      if (!lineItems?.length) {
        setInvoiceGroupIdAttachedToExistingSite(undefined);
      }
    }, [lineItems]);

    React.useEffect(() => {
      if (entitlement) {
        if (!isNotParentProductCompatible && isEnabledEntitlementChangeButton) {
          setAppsParentEntitlement(product?.id, entitlement);
          setIfCartItemValid(product?.id, true);
        } else {
          setIfCartItemValid(product?.id, false);
        }
      }
    }, [product, entitlement, isNotParentProductCompatible, isEnabledEntitlementChangeButton]);

    const validateEntitlement = (query: string | undefined) => {
      setInputValidationError(null);
      if (!query) return;
      if (allowedAppsParentEntitlements && !allowedAppsParentEntitlements?.includes(query)) {
        setInputValidationError('This entitlement does not belong to the selected site');
      }
    };

    return (
      <div className="link-app-to-parent-product-container" data-testid="ppc-link-app-to-parent-product">
        <Field name={'numberOfUsers.' + product?.id} defaultValue={defaultTo(get(entitlement, 'maxUnitCount'), 1)}>
          {() => <></>}
        </Field>
        {isEnabledEntitlementChangeButton ? (
          <ParentProductEntitlementTable
            entitlementDetails={entitlement as ParentEntitlementDetails}
            setIsNotParentProductCompatible={setIsNotParentProductCompatible}
            setEnabledEntitlementChangeButton={setEnabledEntitlementChangeButton}
            offeringLabel={offeringLabel}
            isInvalidInvoiceGroupId={isInvalidIGForAllExistingEntitlements}
            isNotParentProductCompatible={isNotParentProductCompatible}
            isMaxUsersDetailLoading={isMaxUsersDetailLoading}
            onChange={() => {
              setIfCartItemValid(product.id, true);
              setEntitlementSearchKey('');
              removeAppsParentEntitlement(product.id);
            }}
            shouldHideChangeEntitlementButton={shouldHideChangeEntitlementButton}
          />
        ) : (
          <>
            <div className="mt-8 mb-8">
              <SectionMessage appearance="warning">
                <p>
                  An app must be linked to a monthly product subscription. To add this app, search for or select a
                  parent product.
                </p>
              </SectionMessage>
            </div>
            <Field
              label="Parent product entitlement number"
              name={'entitlementNumber.' + `${get(product, 'id')}`}
              isRequired
            >
              {({ fieldProps }) => (
                <>
                  <div className="parent-entitlement-input-search">
                    <Textfield
                      {...fieldProps}
                      placeholder="Enter E-#"
                      name={'entitlementNumber.' + `${get(product, 'id')}`}
                      testId={'entitlement-number-' + `${get(product, 'id')}`}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        fieldProps.onChange(e);
                        const value = e?.target?.value?.trim();
                        setEntitlementSearchKey(value);
                        validateEntitlement(value);
                      }}
                    />
                  </div>
                  {(inputValidationError || entitlementValidationError) && (
                    <div className="entitlement-validation-error">
                      <ErrorMessage testId="entitlement-validation-error">
                        {inputValidationError || entitlementValidationError}
                      </ErrorMessage>
                    </div>
                  )}
                </>
              )}
            </Field>
            <LoadingButton
              appearance="default"
              type="button"
              isDisabled={!!inputValidationError}
              onClick={() => {
                searchEntitlement(entitlementSearchKey);
              }}
              isLoading={entitlementSearchLoading}
              className="ml-16 mb-4 parent-entitlement-button-search"
              data-testId="search-parent-entitlement"
            >
              Search
            </LoadingButton>
          </>
        )}
      </div>
    );
  }
);
